
import {
  defineComponent, ref, computed, onMounted, onUnmounted, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { showToastError, showToastSuccess } from '@/helpers';
import PageHeader from '@/components/layout/PageHeader.vue';
import InputSwitch from 'primevue/inputswitch';
import { UserState } from '@/composables/User';
import { NestApiType, nestCall } from '@/composables/nestApi';
import { useIntegrations } from '@/composables/useIntegrations';
import type { Integration } from '@/composables/useIntegrations';
import { pickLocalizedText, parseCategoryName } from '@/utils/localized-text';
import type { LocalizedText } from '@/utils/localized-text';
import Loader from '@/components/layout/Loader.vue';
import IntegrationPagination from './components/IntegrationPagination.vue';
import IntegrationCard from './components/IntegrationCard.vue';
import IntegrationConfirmDialog from './components/IntegrationConfirmDialog.vue';
import IntegrationDetailsSidebar from './components/IntegrationDetailsSidebar.vue';
import IntegrationConfigModal from './components/IntegrationConfigModal.vue';

interface Category {
  id: string | number;
  name: LocalizedText;
}

export default defineComponent({
  name: 'ShopIntegrations',

  components: {
    PageHeader,
    InputSwitch,
    IntegrationPagination,
    IntegrationCard,
    IntegrationConfirmDialog,
    IntegrationDetailsSidebar,
    IntegrationConfigModal,
    Loader,
  },

  setup() {
    const { t, locale } = useI18n();
    const route = useRoute();
    const router = useRouter();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const confirmDialogRef = ref<any>(null);
    const configModalRef = ref<any>(null);

    const idShop = UserState.activeShop?.id ?? 0;

    const {
      activeIntegrations,
      availableIntegrations,
      loadingActive,
      loadingAvailable,
      activeTotalItems,
      availableTotalItems,
      errorActive,
      errorAvailable,
      loadIntegrations,
      installIntegration,
      uninstallIntegration,
      toggleIntegrationStatus,
      cancelInstallation,
      isIntegrationInstalled,
      getShopIntegrationStatus,
      getIntegrationDetails,
    } = useIntegrations(idShop);

    // --- View state ---
    const activeView = ref<'installed' | 'marketplace'>('marketplace');
    const statusFilter = ref<'all' | 'active' | 'inactive'>('all');

    // --- Filtered integrations ---
    const filteredActiveIntegrations = computed(() => {
      if (statusFilter.value === 'all') return activeIntegrations.value;
      return activeIntegrations.value.filter(
        (i) => getShopIntegrationStatus(i) === statusFilter.value,
      );
    });

    const activeCount = computed(() => activeIntegrations.value.filter(
      (i) => getShopIntegrationStatus(i) === 'active',
    ).length);

    const inactiveCount = computed(() => activeIntegrations.value.filter(
      (i) => getShopIntegrationStatus(i) === 'inactive',
    ).length);

    // --- Details sidebar ---
    const detailsSidebarVisible = ref(false);
    const detailsIntegration = ref<Integration | null>(null);

    const detailsIntegrationStatus = computed(() => {
      if (!detailsIntegration.value) return '';
      return getShopIntegrationStatus(detailsIntegration.value);
    });

    const openDetails = (integration: Integration) => {
      detailsIntegration.value = integration;
      detailsSidebarVisible.value = true;
    };

    const handleConfigureFromSidebar = (integration: Integration) => {
      detailsSidebarVisible.value = false;
      navigateToConfigure(integration);
    };

    // --- Categories ---
    const categories = ref<Category[]>([]);
    const categoryFilter = ref<(string | number)[]>([]);

    const loadCategories = async () => {
      try {
        const response = await nestCall(
          'get',
          NestApiType.CLIENT_API,
          '/integrations/categories',
          {},
          '',
          undefined,
          true,
          { fields: 'id_category,category_name' },
        );

        if (response?.data) {
          categories.value = response.data
            .map((cat: { id_category?: string | number; category_name: string }) => {
              // Parseur `category_name` centralisé (G12).
              const parsedName = parseCategoryName(cat.category_name);
              if (!parsedName) return null;
              return { id: cat.id_category ?? parsedName.fr ?? '', name: parsedName };
            })
            .filter((cat: Category | null): cat is Category => cat !== null);
        }
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };

    const getCategoryDisplayName = (cat: Category): string => pickLocalizedText(cat.name, locale.value as string);

    const isCategorySelected = (catId: string | number): boolean => categoryFilter.value.includes(catId);

    const toggleCategory = (catId: string | number) => {
      const index = categoryFilter.value.indexOf(catId);
      if (index === -1) {
        categoryFilter.value = [...categoryFilter.value, catId];
      } else {
        categoryFilter.value = categoryFilter.value.filter((id) => id !== catId);
      }
      currentPage.value = 1;
      reloadActiveView(); // eslint-disable-line no-use-before-define
    };

    const clearCategories = () => {
      categoryFilter.value = [];
      currentPage.value = 1;
      reloadActiveView(); // eslint-disable-line no-use-before-define
    };

    // --- Search ---
    const searchQuery = ref('');
    let searchTimeout: NodeJS.Timeout | null = null;

    const onSearchInput = () => {
      if (searchTimeout) clearTimeout(searchTimeout);
      searchTimeout = setTimeout(() => {
        currentPage.value = 1;
        reloadActiveView(); // eslint-disable-line no-use-before-define
      }, 500);
    };

    const clearSearch = () => {
      searchQuery.value = '';
      currentPage.value = 1;
      reloadActiveView(); // eslint-disable-line no-use-before-define
    };

    // --- Pagination ---
    const rowsPerPage = ref(12);
    const currentPage = ref(1);

    watch(currentPage, () => {
      loadCatalogue(); // eslint-disable-line no-use-before-define
    });

    // --- Data loading ---

    const loadInstalledIntegrations = () => loadIntegrations(
      'active',
      searchQuery.value,
      categoryFilter.value,
      1,
      100,
    );

    const loadCatalogue = () => loadIntegrations(
      'available',
      searchQuery.value,
      categoryFilter.value,
      currentPage.value,
      rowsPerPage.value,
    );

    const reloadActiveView = () => {
      if (activeView.value === 'installed') {
        loadInstalledIntegrations();
      } else {
        loadCatalogue();
      }
    };

    // --- Helpers ---

    const getPartnerName = (integration: Integration): string => {
      if (!integration.partners_data || integration.partners_data.length === 0) return '';
      return integration.partners_data[0].partner_name || '';
    };

    const hasConfig = (integration: Integration): boolean => !!(
      (integration.config?.fields && integration.config.fields.length > 0)
      || (integration.config?.steps && integration.config.steps.length > 0)
    );

    // T1 — lock the activate toggle for an installed-but-unconfigured integration
    // (config schema present, not completed / connection not verified).
    const isActivationLocked = (integration: Integration): boolean => getShopIntegrationStatus(integration) === 'inactive'
      && hasConfig(integration) && !integration.configured;

    const navigateToConfigure = (integration: Integration) => {
      // T3 — open the configuration as a modal instead of navigating to a page.
      configModalRef.value?.open(integration);
    };

    // --- Action handlers ---

    const handleInstall = (integration: Integration): void => {
      // Defensive guard: `soon` integrations must never be installed even if
      // the call slipped through (e.g. legacy emit or external trigger).
      if (integration.status === 'soon') {
        return;
      }
      const action = integration.external_auth === '1' ? 'installOAuth' : 'install';
      if (confirmDialogRef.value) {
        confirmDialogRef.value.open(integration, action);
      }
    };

    const handleUninstall = (integration: Integration): void => {
      if (confirmDialogRef.value) {
        confirmDialogRef.value.open(integration, 'uninstall');
      }
    };

    const handleToggleStatus = (integration: Integration): void => {
      const currentStatus = getShopIntegrationStatus(integration);
      const action = currentStatus === 'active' ? 'deactivate' : 'activate';
      if (confirmDialogRef.value) {
        confirmDialogRef.value.open(integration, action);
      }
    };

    const handleCancelInstallation = (integration: Integration): void => {
      if (confirmDialogRef.value) {
        confirmDialogRef.value.open(integration, 'cancel');
      }
    };

    const reloadBothLists = async () => {
      await loadInstalledIntegrations();
      await loadCatalogue();
    };

    const handleConfirmAction = async ({ integration, action }: { integration: Integration; action: string }) => {
      try {
        switch (action) {
          case 'install':
          case 'installOAuth':
            {
              const result = await installIntegration(integration);
              if (result.notFound) {
                // L'intégration n'est plus proposée à cette boutique (retirée du
                // catalogue ou portée restreinte) : on ferme la fiche et on
                // rafraîchit les listes pour retirer la carte périmée.
                detailsSidebarVisible.value = false;
                await reloadBothLists();
                return;
              }
              if (action === 'installOAuth' && result.redirectUrl) {
                window.open(result.redirectUrl, '_blank');
              }
              if (result.requiresOAuth) {
                // OAuth flow: the integration stays in pending_auth until the
                // provider redirects back. Refresh the lists and stop here —
                // do NOT open the config modal prematurely.
                await reloadBothLists();
                return;
              }
              if (result.success) {
                // T3 — refresh the lists then open the config modal (no full
                // page reload / navigation to a separate config page).
                await reloadBothLists();
                configModalRef.value?.open(integration);
                return;
              }
            }
            break;
          case 'uninstall':
            {
              const success = await uninstallIntegration(integration);
              if (success) {
                // Full reload to marketplace (sources may have been removed)
                window.location.href = router.resolve({
                  name: 'shop.Integrations',
                }).href;
                return;
              }
            }
            break;
          case 'activate':
          {
            await toggleIntegrationStatus(integration);
            // T5 — activation is only reachable once the integration is fully
            // configured (see T1 gating), so there is no reason to re-open the
            // config form afterwards: return to the marketplace list (full
            // reload — sources may have changed).
            window.location.href = router.resolve({
              name: 'shop.Integrations',
            }).href;
            return;
          }
          case 'deactivate':
          {
            await toggleIntegrationStatus(integration);
            // Full reload to marketplace (sources may have changed)
            window.location.href = router.resolve({
              name: 'shop.Integrations',
            }).href;
            return;
          }
          case 'cancel':
            {
              const success = await cancelInstallation(integration);
              if (success) {
                await reloadBothLists();
              }
            }
            break;
          default:
            break;
        }
      } finally {
        if (confirmDialogRef.value) {
          confirmDialogRef.value.finishLoading();
        }
      }
    };

    // --- OAuth callback ---

    const checkOAuthCallback = () => {
      const installStatus = route.query.install_status as string;
      const error = route.query.error as string;

      if (installStatus === 'error' && error) {
        let errorMessage = t('integrations.errors.installationFailed');
        switch (error) {
          case 'internal_error':
            errorMessage = t('integrations.errors.internalError');
            break;
          case 'auth_failed':
            errorMessage = t('integrations.errors.authFailed');
            break;
          case 'invalid_credentials':
            errorMessage = t('integrations.errors.invalidCredentials');
            break;
          case 'access_denied':
            errorMessage = t('integrations.errors.accessDenied');
            break;
          default:
            errorMessage = t('integrations.errors.genericError');
        }
        showToastError(errorMessage, 5000);
        router.replace({ query: {} });
      } else if (installStatus === 'success') {
        showToastSuccess(t('integrations.success.installed'), 5000);
        router.replace({ query: {} });
      }
    };

    // --- Mount ---

    /**
     * `?focus=slug` (G6) : point d'entrée profond depuis un CTA marketplace.
     * On bascule sur la vue Marketplace, on pré-remplit la recherche avec le
     * slug (pour surfacer la carte) et on ouvre la fiche détaillée si le slug
     * résout une intégration. Best-effort : un slug inconnu ne fait qu'amorcer
     * la recherche, sans erreur bloquante.
     */
    const handleFocusQuery = async () => {
      const rawFocus = route.query.focus;
      const slug = Array.isArray(rawFocus) ? rawFocus[0] : rawFocus;
      if (!slug) return;
      activeView.value = 'marketplace';
      searchQuery.value = String(slug);
      loadCatalogue();
      const details = await getIntegrationDetails(String(slug));
      if (details) {
        openDetails(details);
      }
    };

    // G8 — au switch de vue (installées ⇄ marketplace), on réinitialise la
    // recherche pour ne pas transporter un filtre d'une vue à l'autre (contexte
    // et jeux de données différents), puis on (re)charge la vue cible.
    watch(activeView, () => {
      if (searchQuery.value) searchQuery.value = '';
      reloadActiveView();
    });

    onMounted(() => {
      checkOAuthCallback();
      loadInstalledIntegrations();
      loadCatalogue();
      loadCategories();
      handleFocusQuery();
    });

    onUnmounted(() => {
      if (searchTimeout) clearTimeout(searchTimeout);
    });

    return {
      t,
      confirmDialogRef,
      configModalRef,
      reloadBothLists,
      activeView,
      statusFilter,
      filteredActiveIntegrations,
      activeCount,
      inactiveCount,
      // Installed
      activeIntegrations,
      loadingActive,
      activeTotalItems,
      errorActive,
      errorAvailable,
      loadInstalledIntegrations,
      loadCatalogue,
      getShopIntegrationStatus,
      getPartnerName,
      hasConfig,
      isActivationLocked,
      navigateToConfigure,
      // Marketplace
      availableIntegrations,
      loadingAvailable,
      availableTotalItems,
      searchQuery,
      onSearchInput,
      clearSearch,
      currentPage,
      rowsPerPage,
      isIntegrationInstalled,
      // Categories
      categories,
      categoryFilter,
      getCategoryDisplayName,
      isCategorySelected,
      toggleCategory,
      clearCategories,
      // Details sidebar
      detailsSidebarVisible,
      detailsIntegration,
      detailsIntegrationStatus,
      openDetails,
      handleConfigureFromSidebar,
      // Actions
      handleInstall,
      handleUninstall,
      handleToggleStatus,
      handleCancelInstallation,
      handleConfirmAction,
    };
  },
});
